import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Crown, Zap, Infinity, Star, Sparkles, Coins } from "lucide-react";
import { fetchPlans, startCheckout } from "../game/authService";
import { useAuth } from "../contexts/AuthContext";
import { playClick } from "../game/sound";

// Visual icon per plan id.
const PLAN_ICONS = {
  vip_weekly: Zap,
  vip_monthly: Sparkles,
  vip_lifetime: Crown,
  founder_pack: Star,
  coin_pack_small: Coins,
  coin_pack_medium: Coins,
  coin_pack_large: Coins,
  coin_pack_huge: Coins,
};
const PLAN_BLURB = {
  vip_weekly: "7-day VIP pass. Try out the perks.",
  vip_monthly: "30-day VIP pass. Best value for casual players.",
  vip_lifetime: "VIP forever. Includes the VIP badge & title.",
  founder_pack: "Lifetime VIP + Founder badge + Founder title + the strongest carry-over bonus in the game.",
  coin_pack_small: "Skip the grind on a new beam or title.",
  coin_pack_medium: "Pick up a fresh UFO skin or two.",
  coin_pack_large: "Unlock several shop items in one go. Best starter pack.",
  coin_pack_huge: "Buy the entire shop and still have coins left over.",
};

export default function VipScreen({ onBack }) {
  const { user, loading } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const buy = async (plan) => {
    setErr("");
    if (!user) {
      setErr("Sign in with Google first to purchase VIP.");
      return;
    }
    setBusy(plan.id);
    playClick();
    try {
      const res = await startCheckout(plan.id);
      if (res && res.url) {
        window.location.href = res.url;
      } else {
        setErr("Could not start checkout. Try again.");
        setBusy(null);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="screen vip-screen" data-testid="vip-screen">
      <button
        type="button"
        className="zc-btn icon-back"
        onClick={() => {
          playClick();
          onBack();
        }}
        data-testid="vip-back-button"
      >
        <ArrowLeft size={18} /> Back
      </button>

      <div className="vip-wrap">
        <h1 className="vip-title">
          <Crown size={28} /> VIP & Founder
        </h1>
        <p className="vip-tag">
          Support the game and get 4 upgrade choices, free rerolls, daily crates,
          and exclusive cosmetics. Not pay-to-win — just nice perks.
        </p>

        {user && user.is_vip && (
          <div className="vip-already" data-testid="vip-already">
            <Crown size={18} />
            You're VIP{user.founder ? " · FOUNDER" : ""}
            {user.vip_until ? ` · until ${new Date(user.vip_until).toLocaleDateString()}` : ""}
          </div>
        )}

        {!user && !loading && (
          <div className="vip-already" data-testid="vip-need-login">
            Sign in with Google on the main menu before purchasing.
          </div>
        )}

        <div className="vip-plans" data-testid="vip-plans">
          {plans.filter((p) => p.type !== "coins").map((p) => {
            const Icon = PLAN_ICONS[p.id] || Crown;
            const recurring = p.id === "vip_weekly" || p.id === "vip_monthly";
            const isFounder = p.id === "founder_pack";
            return (
              <div
                key={p.id}
                className={`vip-plan-card ${isFounder ? "founder" : ""}`}
                data-testid={`vip-plan-${p.id}`}
              >
                <div className="vip-plan-icon">
                  <Icon size={28} />
                </div>
                <div className="vip-plan-name">{p.name}</div>
                <div className="vip-plan-price">${p.amount.toFixed(2)}</div>
                {(p.coins_bonus || 0) > 0 && (
                  <div className="vip-coin-bonus" data-testid={`vip-coin-bonus-${p.id}`}>
                    <Coins size={14} /> + {p.coins_bonus.toLocaleString()} coins
                  </div>
                )}
                <div className="vip-plan-blurb">{PLAN_BLURB[p.id] || ""}</div>
                <button
                  type="button"
                  className="zc-btn primary vip-plan-buy"
                  disabled={busy === p.id || !user}
                  onClick={() => buy(p)}
                  data-testid={`vip-buy-${p.id}`}
                >
                  {busy === p.id ? "Opening…" : `Get ${p.name}`}
                </button>
                {recurring && (
                  <div className="vip-plan-note">
                    Note: currently sold as a {p.days}-day pass. Re-purchase to renew.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ---- Coin Packs (Feb 2026) ---- */}
        <CoinPackSection plans={plans} user={user} busy={busy} buy={buy} />

        {err && <div className="vip-err" data-testid="vip-error">{err}</div>}

        <ul className="vip-perks">
          <li><Zap size={14} /> 4 upgrade choices per reward popup (free players get 3)</li>
          <li><Sparkles size={14} /> 1 free reroll every reward popup</li>
          <li><Crown size={14} /> Daily VIP crate — random reward every day</li>
          <li><Coins size={14} /> +5,000 coins every VIP purchase (Founder: +10,000)</li>
          <li><Star size={14} /> +25% XP gain per run (Founder: +50%)</li>
          <li><Zap size={14} /> +100 starting coins every fresh run (Founder: +200)</li>
          <li><Sparkles size={14} /> Carry-Over: keep 1 random power on your next run (Founder: 2)</li>
          <li><Star size={14} /> VIP badge & title next to your name on the leaderboard</li>
          <li><Infinity size={14} /> Exclusive VIP & Founder cosmetics</li>
        </ul>
      </div>
    </div>
  );
}

function CoinPackSection({ plans, user, busy, buy }) {
  const packs = useMemo(
    () => plans.filter((p) => p.type === "coins").sort((a, b) => (a.amount || 0) - (b.amount || 0)),
    [plans],
  );
  if (packs.length === 0) return null;
  // Compute coins-per-dollar to show "Best Value" badge on the highest one.
  const bestId = packs.reduce((best, p) => {
    const cpd = (p.coins || 0) / Math.max(0.01, p.amount || 1);
    if (!best || cpd > best.cpd) return { id: p.id, cpd };
    return best;
  }, null)?.id;
  return (
    <>
      <h2 className="vip-section-h" data-testid="coin-packs-heading">
        <Coins size={20} /> Buy Coins
      </h2>
      <p className="vip-tag" style={{ marginTop: 0 }}>
        Skip the grind. Coins unlock UFO skins, beam colors, badges and titles in the Cosmetics shop.
      </p>
      <div className="vip-plans coin-plans" data-testid="coin-packs">
        {packs.map((p) => {
          const isBest = p.id === bestId;
          return (
            <div
              key={p.id}
              className={`vip-plan-card coin-pack ${isBest ? "best-value" : ""}`}
              data-testid={`coin-pack-${p.id}`}
            >
              {isBest && <div className="coin-pack-best">BEST VALUE</div>}
              <div className="vip-plan-icon coin-icon">
                <Coins size={28} />
              </div>
              <div className="vip-plan-name">{p.name}</div>
              <div className="coin-pack-amount" data-testid={`coin-pack-${p.id}-amount`}>
                {(p.coins || 0).toLocaleString()} coins
              </div>
              <div className="vip-plan-price">${p.amount.toFixed(2)}</div>
              <div className="vip-plan-blurb">{PLAN_BLURB[p.id] || ""}</div>
              <button
                type="button"
                className="zc-btn primary vip-plan-buy"
                disabled={busy === p.id || !user}
                onClick={() => buy(p)}
                data-testid={`coin-buy-${p.id}`}
              >
                {busy === p.id ? "Opening…" : `Buy for $${p.amount.toFixed(2)}`}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}
